/** @jsxImportSource @emotion/react */
import React, {
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { css } from "@emotion/react";
import { Column, Row, Text, TEXT_VARIANTS } from "..";
import { COLORS } from "../../shared/theme";
import {useMediaQuery} from "react-responsive";

type SidebarItem = {
  id: string;
};

type TSidebarContext = {
  selected: Array<[SidebarSectionProps["id"], Array<SidebarItem["id"]>]>;
  updateSelected: (selected: TSidebarContext["selected"]) => void;
};

export const SidebarContext = React.createContext<TSidebarContext>({
  selected: [],
  updateSelected: () => {},
});

type SidebarSectionProps = {
  multi?: boolean;
  id: string;
};

// type SidebarCollapsibleSectionProps = SidebarSectionProps;

type TSidebarSectionContext = {
  multi: boolean;
  selected: Array<SidebarItem["id"]>;
  updateSelected: (selected: Array<SidebarItem["id"]>) => void;
};

export const SidebarSectionContext =
  React.createContext<TSidebarSectionContext>({
    multi: false,
    selected: [],
    updateSelected: () => {},
  });

type SidebarProps = {
  value: TSidebarContext["selected"];
  onChange: (selected: TSidebarContext["selected"]) => void;
};

export const SidebarSection: React.FC<
  React.PropsWithChildren<SidebarSectionProps>
> = ({ multi = false, id, children }) => {
  const { selected: globalSelection, updateSelected } =
    useContext(SidebarContext);
  const selected = globalSelection.find((section) => id === section[0])?.[1] || [];

  return (
    <SidebarSectionContext.Provider
      value={{
        multi,
        selected,
        updateSelected: (selected) => {
          const sectionIndex = globalSelection.findIndex(
            (section) => section[0] === id
          );
          if (sectionIndex === -1)
            updateSelected(globalSelection.concat([[id, selected]]));
          else {
            const updatedSelection = globalSelection.slice();
            updatedSelection[sectionIndex] = [id, selected];
            updateSelected(updatedSelection);
          }
        },
      }}
    >
      <Column gap={5}>{children}</Column>
    </SidebarSectionContext.Provider>
  );
};

export const SidebarSectionListItem: React.FC<
  SidebarItem & {
    icon?: JSX.Element;
    label: string;
    count?: number;
    actions?: (isHovering: boolean) => JSX.Element;
    onClick?: (
      isActive: boolean,
      multi: boolean,
      selected: TSidebarSectionContext["selected"],
      updateSelected: TSidebarSectionContext["updateSelected"],
      id: string
    ) => void;
  }
> = ({ id, label, icon, count, onClick, actions }) => {
  const { selected, updateSelected, multi } = useContext(SidebarSectionContext);
  const [isHovering, setIsHovering] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: 700 });

  const isActive = useMemo(() => selected.includes(id), [selected, id]);

  const handleClick = useCallback(() => {
    if (onClick !== undefined) {
      onClick(isActive, multi, selected, updateSelected, id);
      return;
    }

    if (isActive) {
      updateSelected(selected.filter((_id) => _id !== id));
      return;
    }

    if (multi) updateSelected(selected.concat([id]));
    else updateSelected([id]);
  }, [isActive, multi, selected, updateSelected, id, onClick]);

  return (
    <Row
      mainAxisAlignment="space-between"
      crossAxisAlignment="center"
      role="button"
      gap={5}
      onPointerEnter={() => setIsHovering(true)}
      onPointerLeave={() => setIsHovering(false)}
      css={css`
        transition: all 120ms ease;
        cursor: pointer;
        height: 35px;
        width: 100%;
        padding: 0 5px;
        border-radius: 10px;
        background-color: ${isActive ? COLORS.LIME : "transparent"};

        &:hover, &:focus, &:active {
          background-color: ${isActive ? COLORS.LIME : isMobile ? 'transparent' : COLORS.LIGHT};
        }
      `}
      onClick={handleClick}
    >
      <Row gap={5}>
        {icon}
        <Text
          variant={TEXT_VARIANTS.T20}
          css={css`
            color: ${COLORS.BLACK};
          `}
        >
          {label}
          {count !== undefined && (
            <Text
              as="span"
              variant={TEXT_VARIANTS.T20}
              css={css`
                color: ${COLORS.SILVER};
              `}
            >
              {` ${count}`}
            </Text>
          )}
        </Text>
      </Row>
      {actions !== undefined && actions(isHovering)}
    </Row>
  );
};

export const Sidebar: React.FC<React.PropsWithChildren<SidebarProps>> = ({
  children,
  value,
  onChange,
}) => {

  return (
    <SidebarContext.Provider value={{ selected: value, updateSelected: onChange }}>
      <Column
        gap={25}
        as="aside"
        css={css`
          padding-bottom: 50px;
        `}
      >
        {children}
      </Column>
    </SidebarContext.Provider>
  );
};
